import { FiAlignJustify } from 'react-icons/fi';

export const parallelStructureData = {
  courseId: 'parallel-structure',
  courseType: 'grammar',
  title: 'Parallel Structure',
  icon: FiAlignJustify,
  overview: {
    impact: 'Essential for clear, professional writing',
    focus: 'Maintaining consistent grammatical forms',
    uses: 'Writing Task 1 & 2, Lists, Comparisons',
    duration: '20 minutes',
  },
  sections: [
    {
      title: 'What Is Parallel Structure?',
      content: `<p>Parallel structure means using the same grammatical form for items in a list, comparison, or series. It makes writing clearer and more professional.</p>`,
      examples: [
        { label: 'Not parallel', text: 'She likes swimming, to run, and cooking.' },
        { label: 'Parallel', text: 'She likes swimming, running, and cooking.' },
      ],
    },
    {
      title: 'Common Parallel Patterns',
      content: `<p>Maintain parallel structure in these situations:</p>`,
      table: {
        headers: ['Situation', 'Wrong', 'Correct'],
        rows: [
          ['Lists', 'reading, to write, speaking', 'reading, writing, speaking'],
          ['Comparisons', 'better to act than waiting', 'better to act than to wait'],
          ['Correlatives (not only...but also)', 'not only intelligent but also works hard', 'not only intelligent but also hardworking'],
        ],
      },
    },
    {
      title: 'Parallel Structure with Conjunctions',
      content: `<p>Pay special attention when using correlative conjunctions:</p>`,
      points: [
        'both...and: "Both reading and writing are important."',
        'either...or: "You can either study now or study later."',
        'neither...nor: "Neither the cost nor the quality is acceptable."',
        'not only...but also: "Technology is not only useful but also essential."',
      ],
      warning: 'Whatever structure follows the first part must match the second part!',
    },
  ],
  exercises: [
    {
      title: 'Identify Parallel Structure Errors',
      type: 'select',
      instructions: 'Which sentence has correct parallel structure?',
      questions: [
        {
          text: 'Choose the CORRECT sentence:',
          options: [
            'She likes swimming, running, and cycling.',
            'She likes swimming, to run, and cycling.',
            'She likes swimming, running, and to cycle.',
          ],
          answer: 'She likes swimming, running, and cycling.',
          explanation: 'All items should be gerunds (-ing forms).',
        },
        {
          text: 'Choose the CORRECT sentence:',
          options: [
            'The course will teach you to read, write, and speak effectively.',
            'The course will teach you reading, writing, and to speak.',
            'The course will teach you to read, writing, and speak.',
          ],
          answer: 'The course will teach you to read, write, and speak effectively.',
          explanation: 'All items should be infinitives (to + verb base).',
        },
        {
          text: 'Choose the CORRECT sentence:',
          options: [
            'Not only is she intelligent but also hardworking.',
            'Not only is she intelligent but also she works hard.',
            'Not only she is intelligent but also hardworking.',
          ],
          answer: 'Not only is she intelligent but also hardworking.',
          explanation: 'Matching adjectives after both parts.',
        },
      ],
    },
    {
      title: 'Fix the Parallel Structure',
      type: 'fill-in',
      instructions: 'Rewrite the sentence with correct parallel structure.',
      questions: [
        {
          text: 'Wrong: "The job requires creativity, being organized, and you must communicate well."',
          answer: 'The job requires creativity, organization, and communication skills.',
          explanation: 'Use consistent noun forms in the list.',
        },
        {
          text: 'Wrong: "She not only studies hard but also is working part-time."',
          answer: 'She not only studies hard but also works part-time.',
          explanation: 'Match verb tense and structure after both parts.',
        },
        {
          text: 'Wrong: "To learn English requires patience, practice, and being persistent."',
          answer: 'To learn English requires patience, practice, and persistence.',
          explanation: 'Use consistent noun forms.',
        },
        {
          text: 'Wrong: "The essay was well-researched, clearly written, and showed insight."',
          answer: 'The essay was well-researched, clearly written, and insightful.',
          explanation: 'Use consistent adjective forms.',
        },
      ],
    },
    {
      title: 'Complete with Parallel Form',
      type: 'select',
      instructions: 'Choose the form that maintains parallel structure.',
      questions: [
        {
          text: 'The manager values honesty, integrity, and ___.',
          options: ['reliability', 'being reliable', 'to be reliable'],
          answer: 'reliability',
          explanation: 'Match the noun forms (honesty, integrity).',
        },
        {
          text: 'Students must attend lectures, complete assignments, and ___.',
          options: ['participate in discussions', 'participating in discussions', 'to participate in discussions'],
          answer: 'participate in discussions',
          explanation: 'Match the verb forms (attend, complete).',
        },
        {
          text: 'The speech was both informative and ___.',
          options: ['engaging', 'engaged the audience', 'to engage'],
          answer: 'engaging',
          explanation: 'After "both...and," match adjective with adjective.',
        },
        {
          text: 'You can either study now or ___.',
          options: ['study later', 'studying later', 'to study later'],
          answer: 'study later',
          explanation: 'After "either...or," match verb forms.',
        },
      ],
    },
    {
      title: 'Write Parallel Sentences',
      type: 'writing',
      instructions: 'Write sentences with correct parallel structure.',
      questions: [
        {
          text: 'Write a sentence using "both...and" with two adjectives.',
          example: 'The new policy is both effective and fair.',
          tip: 'Structure: Subject + verb + both + adjective + and + adjective.',
        },
        {
          text: 'Write a sentence listing three actions (verbs).',
          example: 'To succeed, you must plan carefully, work hard, and stay focused.',
          tip: 'All verbs should be in the same form.',
        },
        {
          text: 'Write a sentence using "not only...but also".',
          example: 'Technology not only improves efficiency but also reduces costs.',
          tip: 'Whatever follows "not only" should match what follows "but also."',
        },
      ],
    },
  ],
  tips: [
    'Check lists for consistent grammatical forms',
    'Pay attention to "and", "or", and correlative conjunctions',
    'Read your writing aloud to catch awkward structures',
  ],
  dailyPractice: [
    'Review your writing for parallel structure',
    'Practice with correlative conjunctions',
    'Identify parallel structures in professional writing',
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar Concepts',
  aiContext: 'Parallel Structure: Using consistent grammatical forms in lists, comparisons, and with conjunctions for clear, professional writing.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...parallelStructureData,
      title: parallelStructureData.title + ' (Foundations)',
      overview: {
        ...parallelStructureData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: parallelStructureData.sections.slice(0, Math.max(2, Math.ceil(parallelStructureData.sections.length / 2))),
      exercises: parallelStructureData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...parallelStructureData,
      title: parallelStructureData.title + ' (Advanced)',
      overview: {
        ...parallelStructureData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: parallelStructureData.sections,
      exercises: parallelStructureData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(parallelStructureData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return parallelStructureData;
}
